import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Upload, FileText, Check, ArrowLeft, ArrowRight, RotateCcw, Cloud, CloudOff, Building2, Plus, Trash2, Star, GripVertical, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// LocalStorage mirror — kept as an offline cache. The cloud draft is the source of truth.
const DRAFT_KEY = "easystay:new-listing:v1";

const AMENITY_SUGGESTIONS = ["Wi-Fi", "Pool", "Parking", "Breakfast", "Air conditioning", "Kitchen", "Workspace", "TV", "Gym", "Lake view", "BBQ", "Airport shuttle"];
const CITIES = ["Lusaka", "Livingstone", "Ndola", "Kitwe", "Kabwe", "Siavonga", "Chipata", "Solwezi"];

const STEPS = [
  { key: "basics", label: "Basics", body: "Property type, location, description" },
  { key: "photos", label: "Photos & amenities", body: "Show off the space" },
  { key: "pricing", label: "Pricing & keys", body: "Rates, key handoff, payments" },
];

const EMPTY = {
  title: "", type: "guest_house", city: "Lusaka", address: "", description: "",
  price_per_hour_zmw: 150, price_per_night_zmw: 1500, min_hours: 2, max_guests: 2,
  key_options: ["digital", "physical"], physical_key_address: "",
  accepted_payments: ["mtn", "airtel", "zamtel", "card"], amenities: [], photos: [],
  // Multi-room support: only relevant when `type` is a multi-room type
  // (hotel/lodge/guest_house/motel). One row per room type with the
  // spec-defined defaults + a "custom" fallback. Empty = single-unit property.
  room_types: [],
};

// Multi-room property types. When the wizard sees one of these in
// `form.type`, the "Room types & counts" (or "Sub-types" for venue types)
// section appears inline on the Basics step, directly under the property-
// type select — hosts pick the type once and configure inventory in the
// same view.
// House + apartment are single-unit and skip the section entirely.
const MULTI_ROOM_TYPES = new Set(["hotel", "lodge", "guest_house", "motel", "event_centre", "conference_room"]);
// Venue-style types are booked strictly per hour (a conference room or
// event hall doesn't have a "night" concept — an all-day booking is still
// hourly). Everything else keeps the hybrid hourly + nightly pricing.
const HOUR_ONLY_TYPES = new Set(["event_centre", "conference_room"]);
// Default sub-types shown as suggestion chips inside the room-type
// picker. Falls back to `ROOM_TYPE_SUGGESTIONS_DEFAULT` for the classic
// lodging types (hotel / lodge / guest house / motel).
const ROOM_TYPE_SUGGESTIONS_DEFAULT = ["Standard", "Deluxe", "Suite", "Family Suite"];
const ROOM_TYPE_SUGGESTIONS_BY_TYPE = {
  event_centre: ["Main Hall", "Banquet Room", "Outdoor Venue", "VIP Lounge"],
  conference_room: ["Boardroom", "Training Room", "Auditorium", "Breakout Room"],
};
const suggestionsFor = (type) => ROOM_TYPE_SUGGESTIONS_BY_TYPE[type] || ROOM_TYPE_SUGGESTIONS_DEFAULT;
// Standard room-type suggestions per spec. Hosts can pick these or type
// their own — the field is free-text.
const ROOM_TYPE_SUGGESTIONS = ROOM_TYPE_SUGGESTIONS_DEFAULT;

export default function NewListing() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [draftLoadedAt, setDraftLoadedAt] = useState(null);   // ISO string of the restored draft's saved_at
  const [draftSource, setDraftSource] = useState(null);       // "cloud" | "local" | null
  const [cloudStatus, setCloudStatus] = useState("idle");     // idle | saving | saved | offline
  const [amenityInput, setAmenityInput] = useState("");
  const [uploading, setUploading] = useState(false);
  // Per-room-type upload progress. Shape: {[rtIdx]: {current: filename,
  // total: N, done: M, percent: 0..100}}. Cleared to null when the loop
  // completes (success OR failure) so the progress bar disappears cleanly.
  const [rtProgress, setRtProgress] = useState({});
  const [saving, setSaving] = useState(false);
  const initialLoad = useRef(false);
  const skipNextSave = useRef(false);  // set right after restoring so we don't immediately re-save

  // Restore any saved draft — prefer cloud, fall back to localStorage.
  useEffect(() => {
    (async () => {
      if (initialLoad.current) return;
      initialLoad.current = true;
      // 1. Try the cloud
      try {
        const { data } = await axios.get(`${API}/my/wizard-draft`);
        if (data?.exists && data.form) {
          skipNextSave.current = true;
          setForm({ ...EMPTY, ...data.form });
          setStep(Math.min(STEPS.length - 1, Math.max(0, data.step || 0)));
          setDraftLoadedAt(data.saved_at);
          setDraftSource("cloud");
          setCloudStatus("saved");
          // Refresh the local mirror
          try {
            localStorage.setItem(DRAFT_KEY, JSON.stringify({
              form: data.form, step: data.step || 0, savedAt: data.saved_at,
            }));
          } catch { /* quota — silent */ }
          return;
        }
      } catch { /* unauth or network — try local */ }
      // 2. Fall back to localStorage
      try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (parsed?.form && parsed?.savedAt) {
          skipNextSave.current = true;
          setForm({ ...EMPTY, ...parsed.form });
          setStep(Math.min(STEPS.length - 1, Math.max(0, parsed.step || 0)));
          setDraftLoadedAt(parsed.savedAt);
          setDraftSource("local");
        }
      } catch { /* corrupt — ignore */ }
    })();
  }, []);

  // Debounced save: 800ms after last edit → PUT to cloud + write local mirror.
  const saveDraft = useCallback(async (nextForm, nextStep) => {
    // Local mirror is synchronous and always attempted.
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        form: nextForm, step: nextStep, savedAt: new Date().toISOString(),
      }));
    } catch { /* quota — silent */ }
    // Cloud save — best-effort; degrades to "offline" but never breaks the form.
    setCloudStatus("saving");
    try {
      await axios.put(`${API}/my/wizard-draft`, { form: nextForm, step: nextStep });
      setCloudStatus("saved");
    } catch (e) {
      // 401 → user is browsing without logging in; the form still works, just no cloud sync.
      setCloudStatus(e?.response?.status === 401 ? "idle" : "offline");
    }
  }, []);

  useEffect(() => {
    if (skipNextSave.current) { skipNextSave.current = false; return; }
    const t = setTimeout(() => saveDraft(form, step), 800);
    return () => clearTimeout(t);
  }, [form, step, saveDraft]);

  const clearDraft = async () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    try { await axios.delete(`${API}/my/wizard-draft`); } catch { /* offline is fine */ }
    skipNextSave.current = true;
    setForm(EMPTY);
    setStep(0);
    setDraftLoadedAt(null);
    setDraftSource(null);
    setCloudStatus("idle");
    toast.success("Draft cleared");
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/uploads/image`, fd);
        paths.push(data.path);
      }
      set("photos", [...form.photos, ...paths]);
      toast.success(`Uploaded ${paths.length} photo(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removePhoto = (p) => set("photos", form.photos.filter((x) => x !== p));

  // Per-room-type photo helpers. Uploads go to /api/uploads/image (same
  // object storage the main photo grid uses). Photos are attached to a
  // specific room-type row so every physical room of that type shares
  // the same gallery on the guest-facing listing. Falls back to the
  // property's main photos server-side if this array is empty.
  const uploadRoomTypePhotos = async (idx, files) => {
    if (!files?.length) return;
    const arr = Array.from(files);
    setUploading(true);
    setRtProgress((p) => ({ ...p, [idx]: { total: arr.length, done: 0, current: arr[0].name, percent: 0, retry: 0 } }));
    // Auto-retry with exponential backoff — critical on Zambian mobile
    // networks where a single dropped packet can 500 an otherwise-fine
    // upload. Backoffs sit at 500ms / 1.5s / 4s so a transient blip clears
    // within 6s and a full 3-retry cycle finishes inside ~10s.
    const MAX_RETRIES = 3;
    const BACKOFFS_MS = [500, 1500, 4000];
    try {
      const paths = [];
      for (let i = 0; i < arr.length; i++) {
        const f = arr[i];
        setRtProgress((p) => ({ ...p, [idx]: { ...(p[idx] || {}), current: f.name, percent: 0, retry: 0 } }));
        let uploadedPath = null;
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          try {
            if (attempt > 0) {
              const delayMs = BACKOFFS_MS[attempt - 1];
              const startedAt = Date.now();
              setRtProgress((p) => ({
                ...p,
                [idx]: { ...(p[idx] || {}), retry: attempt, percent: 0, retryRemainingMs: delayMs },
              }));
              // Tick the countdown every 100ms so the host sees the timer
              // draining live instead of a static amber state during the
              // backoff. Interval is cleared before the retry starts.
              const tick = setInterval(() => {
                setRtProgress((p) => {
                  const cur = p[idx];
                  if (!cur) return p;
                  const remaining = Math.max(0, delayMs - (Date.now() - startedAt));
                  return { ...p, [idx]: { ...cur, retryRemainingMs: remaining } };
                });
              }, 100);
              await new Promise((r) => setTimeout(r, delayMs));
              clearInterval(tick);
              setRtProgress((p) => ({
                ...p,
                [idx]: { ...(p[idx] || {}), retryRemainingMs: 0 },
              }));
            }
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await axios.post(`${API}/uploads/image`, fd, {
              onUploadProgress: (evt) => {
                const total = evt.total || evt.loaded;
                const percent = total ? Math.round((100 * evt.loaded) / total) : 0;
                setRtProgress((p) => ({ ...p, [idx]: { ...(p[idx] || {}), percent } }));
              },
            });
            uploadedPath = data.path;
            break;
          } catch (e) {
            // 4xx = user error (bad file, too large, unauth) — no point
            // retrying, surface it immediately. 5xx / network = transient,
            // fall through to backoff.
            const status = e.response?.status;
            if (status && status >= 400 && status < 500) throw e;
            if (attempt >= MAX_RETRIES) throw e;
          }
        }
        paths.push(uploadedPath);
        setRtProgress((p) => ({ ...p, [idx]: { ...(p[idx] || {}), done: i + 1, percent: 100, retry: 0 } }));
      }
      const next = [...form.room_types];
      const current = Array.isArray(next[idx].photos) ? next[idx].photos : [];
      next[idx] = { ...next[idx], photos: [...current, ...paths] };
      set("room_types", next);
      toast.success(`Uploaded ${paths.length} photo(s) to ${next[idx].name || "room type"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed after 3 retries — check connection and try again");
    } finally {
      setUploading(false);
      // Clear the progress row on the next tick so the completed 100% bar
      // is visible for a beat before it disappears — feels more polished
      // than snapping straight to the thumbnail grid.
      setTimeout(() => setRtProgress((p) => ({ ...p, [idx]: null })), 400);
    }
  };

  const removeRoomTypePhoto = (idx, path) => {
    const next = [...form.room_types];
    const current = Array.isArray(next[idx].photos) ? next[idx].photos : [];
    next[idx] = { ...next[idx], photos: current.filter((p) => p !== path) };
    set("room_types", next);
  };

  // Drag-reorder / cover-pin helpers. Both grids (main property photos +
  // per-room-type photos) share the same semantics: `photos[0]` is the
  // hero shown in search results / OG shares, so pinning a cover is just
  // "move to index 0" and dragging is a full array reshuffle. Uses the
  // browser's native HTML5 drag-and-drop API — no dependency added.
  const arrayReorder = (arr, from, to) => {
    if (from === to || from < 0 || from >= arr.length || to < 0 || to >= arr.length) return arr;
    const copy = [...arr];
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);
    return copy;
  };
  const reorderMainPhotos = (from, to) =>
    set("photos", arrayReorder(form.photos, from, to));
  const pinMainCover = (idx) => reorderMainPhotos(idx, 0);
  const reorderRoomTypePhotos = (rtIdx, from, to) => {
    const next = [...form.room_types];
    const current = Array.isArray(next[rtIdx].photos) ? next[rtIdx].photos : [];
    next[rtIdx] = { ...next[rtIdx], photos: arrayReorder(current, from, to) };
    set("room_types", next);
  };
  const pinRoomTypeCover = (rtIdx, idx) => reorderRoomTypePhotos(rtIdx, idx, 0);

  const validateStep = () => {
    if (step === 0) {
      if (!form.title.trim()) return "Give your property a title";
      if (!form.address.trim()) return "Add a street address";
      if (!form.description.trim()) return "Describe the property";
      return null;
    }
    if (step === 1) {
      // Photos and amenities are recommended but not blocking — hosts can add later.
      return null;
    }
    if (step === 2) {
      if (form.price_per_hour_zmw < 1) return "Add an hourly price";
      // Nightly rate isn't relevant for event centres / conference rooms —
      // they're strictly hourly. Skip the nightly check for those types.
      if (!HOUR_ONLY_TYPES.has(form.type) && form.price_per_night_zmw < 1) return "Add a nightly price";
      if (form.key_options.length === 0) return "Pick at least one key option";
      if (form.key_options.includes("physical") && !form.physical_key_address.trim()) {
        return "Add the physical key collection address";
      }
      if (form.accepted_payments.length === 0) return "Accept at least one payment method";
      return null;
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validateStep();
    if (err) { toast.error(err); return; }
    try {
      setSaving(true);
      const { data } = await axios.post(`${API}/properties`, form);
      // Server has already deleted the cloud draft on successful publish; wipe local mirror too.
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      // Route the host back to their own dashboard (NOT /property/:id — that's
      // the guest-facing booking page). We pass state so the dashboard can
      // render a "Listing submitted 🎉" celebration banner + a "View my listing"
      // link, giving the host a discoverable path to the guest-facing view on
      // their own terms without dropping them into a booking checkout.
      const hasRoomTypes = Array.isArray(form.room_types) && form.room_types.length > 0;
      nav("/host/dashboard", {
        state: {
          justPublishedId: data.id,
          justPublishedTitle: form.title,
          justPublishedStatus: data.status,
          justPublishedHasRoomTypes: hasRoomTypes,
          justPublishedRoomTypeCount: hasRoomTypes ? form.room_types.length : 0,
        },
        replace: true,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const progressPct = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 lg:px-8">
      <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Add a new listing</h1>
      <p className="mt-2 text-slate-600">3 quick steps — around 2 minutes.</p>
      <Link
        to="/host/guidelines"
        target="_blank"
        data-testid="new-listing-guidelines-link"
        className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-copper/10 px-3 py-1.5 text-xs font-semibold text-copper hover:bg-copper/15"
      >
        <FileText className="h-3.5 w-3.5" /> Read the listing guidelines before you start
      </Link>

      {draftLoadedAt && (
        <div
          data-testid="draft-restored-banner"
          className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-copper/20 bg-copper/5 px-4 py-3 text-sm"
        >
          <div className="text-slate-700">
            <b>Draft restored{draftSource === "cloud" ? " from the cloud" : " from this device"}.</b>{" "}
            We saved your progress on {new Date(draftLoadedAt).toLocaleString()} — carry on from Step {step + 1}.
          </div>
          <button
            type="button"
            onClick={clearDraft}
            data-testid="clear-draft-btn"
            className="btn-outline text-xs !py-1.5"
          >
            <RotateCcw className="mr-1 inline h-3.5 w-3.5" /> Start over
          </button>
        </div>
      )}

      {/* Cloud sync indicator */}
      <div className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest"
           data-testid={`cloud-status-${cloudStatus}`}>
        {cloudStatus === "saving" && (
          <><Cloud className="h-3 w-3 animate-pulse text-slate-400" /><span className="text-slate-400">Saving to cloud…</span></>
        )}
        {cloudStatus === "saved" && (
          <><Cloud className="h-3 w-3 text-forest" /><span className="text-forest">Saved to cloud</span></>
        )}
        {cloudStatus === "offline" && (
          <><CloudOff className="h-3 w-3 text-amber-500" /><span className="text-amber-600">Offline — saved locally, will sync when you reconnect</span></>
        )}
      </div>

      {/* Progress bar */}
      <div className="mt-8" data-testid="wizard-progress">
        <div className="mb-3 flex items-center justify-between text-xs font-semibold uppercase tracking-widest text-slate-500">
          <span>Step {step + 1} of {STEPS.length}</span>
          <span>{Math.round(progressPct)}% complete</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full bg-copper transition-all duration-300" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {STEPS.map((s, i) => (
            <div
              key={s.key}
              data-testid={`wizard-step-${s.key}`}
              className={`rounded-2xl border p-3 text-left transition ${
                i === step ? "border-copper bg-copper/5"
                : i < step ? "border-slate-200 bg-white"
                : "border-slate-100 bg-slate-50 opacity-70"
              }`}
            >
              <div className="flex items-center gap-2">
                <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ${
                  i < step ? "bg-forest text-white"
                  : i === step ? "bg-copper text-white"
                  : "bg-slate-200 text-slate-500"
                }`}>
                  {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </div>
                <span className="text-sm font-semibold text-slate-900">{s.label}</span>
              </div>
              <div className="mt-1 hidden text-xs text-slate-500 sm:block">{s.body}</div>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-6">
        {/* STEP 1 — Basics */}
        {step === 0 && (
          <div className="rounded-3xl bg-white p-6 card-shadow space-y-4" data-testid="wizard-panel-basics">
            <h3 className="font-display text-lg font-bold">Property basics</h3>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Title *</label>
              <input data-testid="lst-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" placeholder="Riverside Lodge – Livingstone" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Type *</label>
                <select data-testid="lst-type" value={form.type} onChange={(e) => set("type", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white">
                  <option value="hotel">Hotel</option>
                  <option value="lodge">Lodge</option>
                  <option value="guest_house">Guest house</option>
                  <option value="motel">Motel</option>
                  <option value="house">House</option>
                  <option value="apartment">Apartment</option>
                  <option value="event_centre">Event Centre</option>
                  <option value="conference_room">Conference Room</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">City *</label>
                <select data-testid="lst-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white">
                  {CITIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Room types & counts — only for multi-room property types
                (hotel/lodge/guest_house/motel). Skipped for single-unit
                types (house/apartment). Rendered inline on Basics so hosts
                configure their room inventory in the same view they pick
                the property type. Each row becomes an independently-
                bookable listing on submit; backend fans out one listing
                per room type with its own inventory. */}
            {MULTI_ROOM_TYPES.has(form.type) && (
              <div className="rounded-2xl border border-copper/20 bg-copper/5 p-4 space-y-4" data-testid="wizard-room-types">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="font-display text-base font-bold flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-copper" />
                      {HOUR_ONLY_TYPES.has(form.type) ? "Sub-types & counts" : "Room types & counts"}
                    </h4>
                    <p className="mt-1 text-sm text-slate-600">
                      {HOUR_ONLY_TYPES.has(form.type)
                        ? "Pick from the standard sub-types below or add your own. Each row becomes its own bookable listing — guests reserve them by the hour, and we allocate a specific space when the digital key is sent."
                        : "Pick from the standard types below or add your own. Each row becomes its own bookable listing — guests see and reserve them individually, and we allocate a specific room number when the digital key is sent."}
                    </p>
                  </div>
                </div>

                {/* Suggestion chips — driven by the property type */}
                <div className="flex flex-wrap gap-2">
                  {suggestionsFor(form.type).filter(
                    (s) => !form.room_types.some((r) => r.name.toLowerCase() === s.toLowerCase())
                  ).map((s) => (
                    <button
                      type="button"
                      key={s}
                      data-testid={`room-type-suggest-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() =>
                        set("room_types", [...form.room_types, { name: s, count: 1, photos: [] }])
                      }
                      className="rounded-full border border-copper/40 bg-white px-3 py-1.5 text-xs font-semibold text-copper hover:bg-copper/10"
                    >
                      <Plus className="mr-1 inline h-3 w-3" />
                      {s}
                    </button>
                  ))}
                  <button
                    type="button"
                    data-testid="room-type-add-custom"
                    onClick={() =>
                      set("room_types", [...form.room_types, { name: "", count: 1, photos: [] }])
                    }
                    className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <Plus className="mr-1 inline h-3 w-3" />
                    Custom type
                  </button>
                </div>

                {/* Room type rows */}
                {form.room_types.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-4 text-center text-sm text-slate-500">
                    No room types yet — tap a suggestion above to add one.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {form.room_types.map((rt, idx) => (
                      <div
                        key={idx}
                        data-testid={`room-type-row-${idx}`}
                        className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3"
                      >
                        <div className="grid grid-cols-[1fr,110px,44px] gap-2">
                          <input
                            data-testid={`room-type-name-${idx}`}
                            value={rt.name}
                            onChange={(e) => {
                              const next = [...form.room_types];
                              next[idx] = { ...next[idx], name: e.target.value };
                              set("room_types", next);
                            }}
                            placeholder="e.g. Deluxe"
                            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-copper focus:bg-white"
                          />
                          <input
                            data-testid={`room-type-count-${idx}`}
                            type="number"
                            min={1}
                            value={rt.count}
                            onChange={(e) => {
                              const next = [...form.room_types];
                              next[idx] = { ...next[idx], count: Math.max(1, parseInt(e.target.value) || 1) };
                              set("room_types", next);
                            }}
                            className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-copper focus:bg-white"
                            placeholder="Rooms"
                          />
                          <button
                            type="button"
                            data-testid={`room-type-remove-${idx}`}
                            onClick={() =>
                              set("room_types", form.room_types.filter((_, i) => i !== idx))
                            }
                            className="flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-400 hover:border-red-200 hover:bg-red-50 hover:text-red-500"
                            aria-label="Remove room type"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Per-room-type photos — every room of this type
                            shares this gallery on the guest-facing listing.
                            Falls back to the property's main photos when
                            left empty. */}
                        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                                Photos for this room type
                              </div>
                              <div className="mt-0.5 text-[11px] text-slate-500">
                                Every {(rt.name || "room").toLowerCase()} room shares these. Leave empty to use the property's main photos.
                              </div>
                            </div>
                            <label
                              htmlFor={`rt-photos-${idx}`}
                              className="cursor-pointer rounded-full border border-copper/40 bg-white px-3 py-1.5 text-xs font-semibold text-copper hover:bg-copper/10"
                              data-testid={`room-type-photo-upload-btn-${idx}`}
                            >
                              <Upload className="mr-1 inline h-3 w-3" />
                              {uploading ? "Uploading…" : "Add photos"}
                              <input
                                id={`rt-photos-${idx}`}
                                data-testid={`room-type-photo-input-${idx}`}
                                type="file"
                                multiple
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => uploadRoomTypePhotos(idx, e.target.files)}
                              />
                            </label>
                          </div>
                          {rtProgress[idx] && (
                            <div
                              className="mt-3 rounded-lg bg-white p-2 ring-1 ring-slate-200"
                              data-testid={`room-type-photo-progress-${idx}`}
                            >
                              <div className="flex items-center justify-between gap-2 text-[10px] font-semibold text-slate-700">
                                <span className="truncate">{rtProgress[idx].current}</span>
                                <span className="flex-shrink-0 text-slate-500">
                                  {rtProgress[idx].retry > 0 && rtProgress[idx].retryRemainingMs > 0 ? (
                                    <span
                                      className="inline-flex items-center gap-1 text-amber-600"
                                      data-testid={`room-type-photo-retry-countdown-${idx}`}
                                    >
                                      <Clock className="h-2.5 w-2.5" />
                                      Retrying in {(rtProgress[idx].retryRemainingMs / 1000).toFixed(1)}s
                                    </span>
                                  ) : (
                                    <>
                                      {rtProgress[idx].retry > 0 ? `Retry ${rtProgress[idx].retry}/3 · ` : ""}
                                      {rtProgress[idx].done} of {rtProgress[idx].total} · {rtProgress[idx].percent}%
                                    </>
                                  )}
                                </span>
                              </div>
                              <div className="mt-1 h-1 overflow-hidden rounded-full bg-slate-200">
                                <div
                                  data-testid={`room-type-photo-progress-bar-${idx}`}
                                  className={`h-full transition-all duration-200 ${rtProgress[idx].retry > 0 ? "bg-amber-500" : "bg-copper"}`}
                                  style={{ width: `${rtProgress[idx].percent}%` }}
                                />
                              </div>
                            </div>
                          )}
                          {Array.isArray(rt.photos) && rt.photos.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2" data-testid={`room-type-photo-grid-${idx}`}>
                              {rt.photos.map((p, pi) => {
                                const src = p.startsWith("http") ? p : `${API}/files/${p}`;
                                const isCover = pi === 0;
                                return (
                                  <div
                                    key={p}
                                    data-testid={`room-type-photo-${idx}-${pi}`}
                                    className={`group relative aspect-square w-16 overflow-hidden rounded-lg ring-2 transition ${isCover ? "ring-copper" : "ring-transparent"}`}
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.effectAllowed = "move";
                                      e.dataTransfer.setData("text/plain", String(pi));
                                    }}
                                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      const from = parseInt(e.dataTransfer.getData("text/plain"));
                                      if (!isNaN(from) && from !== pi) reorderRoomTypePhotos(idx, from, pi);
                                    }}
                                    title="Drag to reorder · star to pin as cover"
                                  >
                                    <img src={src} alt="" className="h-full w-full object-cover" />
                                    {/* Drag handle hint (top-left) — visible on hover */}
                                    <div className="pointer-events-none absolute left-0.5 top-0.5 rounded-full bg-black/50 p-0.5 text-white opacity-0 transition group-hover:opacity-100">
                                      <GripVertical className="h-2.5 w-2.5" />
                                    </div>
                                    {/* Cover pin (bottom-left) */}
                                    <button
                                      type="button"
                                      data-testid={`room-type-photo-cover-${idx}-${pi}`}
                                      onClick={() => pinRoomTypeCover(idx, pi)}
                                      disabled={isCover}
                                      aria-label={isCover ? "Cover photo" : "Set as cover"}
                                      className={`absolute bottom-0.5 left-0.5 flex h-5 w-5 items-center justify-center rounded-full text-white transition ${isCover ? "bg-copper" : "bg-black/60 opacity-0 group-hover:opacity-100 hover:bg-copper"}`}
                                    >
                                      <Star className={`h-3 w-3 ${isCover ? "fill-white" : ""}`} />
                                    </button>
                                    <button
                                      type="button"
                                      data-testid={`room-type-photo-remove-${idx}-${p.split("/").pop()}`}
                                      onClick={() => removeRoomTypePhoto(idx, p)}
                                      className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                                      aria-label="Remove photo"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {Array.isArray(rt.photos) && rt.photos.length > 0 && (
                            <div
                              data-testid={`room-type-og-preview-${idx}`}
                              className="mt-3 flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-2 shadow-sm"
                            >
                              <img
                                src={rt.photos[0].startsWith("http") ? rt.photos[0] : `${API}/files/${rt.photos[0]}`}
                                alt=""
                                className="h-14 w-14 flex-shrink-0 rounded-lg object-cover"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-xs font-semibold text-slate-900">
                                  {(form.title || "Your property").trim()} — {rt.name || "Room"}
                                </div>
                                <div className="mt-0.5 truncate text-[10px] text-slate-500">
                                  {form.city} · easystayzm.com
                                </div>
                                <div className="mt-0.5 truncate text-[10px] text-slate-600">
                                  {(form.description || "").slice(0, 80).trim()
                                    || "Book on EasyStay — mobile money or card, digital or physical key."}
                                </div>
                              </div>
                              <span className="flex-shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-emerald-700">
                                Share preview
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500">
                      {form.room_types.reduce((acc, r) => acc + (parseInt(r.count) || 0), 0)} rooms across {form.room_types.length} room type{form.room_types.length === 1 ? "" : "s"}.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Street address *</label>
              <input data-testid="lst-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" placeholder="12 Kudu Rd, Kabulonga" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Description *</label>
              <textarea data-testid="lst-desc" value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" placeholder="Cosy lodge minutes from the falls. Garden, pool, secure parking…" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Max guests</label>
              <input data-testid="lst-guests" type="number" min={1} value={form.max_guests} onChange={(e) => set("max_guests", parseInt(e.target.value) || 1)} className="mt-1 w-32 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" />
            </div>
          </div>
        )}

        {/* STEP 2 — Photos & amenities */}
        {step === 1 && (
          <div className="space-y-6" data-testid="wizard-panel-photos">
            <div className="rounded-3xl bg-white p-6 card-shadow space-y-4">
              <h3 className="font-display text-lg font-bold">Photos</h3>
              <p className="text-xs text-slate-500">Aim for 3+ landscape photos taken in daylight — bright interiors convert 3× better.</p>
              <label htmlFor="lst-photos" className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center hover:border-copper">
                <Upload className="h-8 w-8 text-slate-400" />
                <div className="mt-2 text-sm font-medium text-slate-700">{uploading ? "Uploading…" : "Click to upload photos"}</div>
                <div className="text-xs text-slate-500">JPG / PNG / WebP · Max 8MB each</div>
                <input id="lst-photos" data-testid="lst-photos-input" type="file" multiple accept="image/*" className="hidden" onChange={(e) => upload(e.target.files)} />
              </label>
              {form.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-3" data-testid="main-photo-grid">
                  {form.photos.map((p, i) => {
                    const isCover = i === 0;
                    return (
                      <div
                        key={p}
                        data-testid={`main-photo-${i}`}
                        className={`group relative aspect-square overflow-hidden rounded-2xl ring-2 transition ${isCover ? "ring-copper" : "ring-transparent"}`}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", String(i));
                        }}
                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const from = parseInt(e.dataTransfer.getData("text/plain"));
                          if (!isNaN(from) && from !== i) reorderMainPhotos(from, i);
                        }}
                        title="Drag to reorder · star to pin as cover"
                      >
                        <img src={`${API}/files/${p}`} alt="" className="h-full w-full object-cover" />
                        {/* Drag handle hint */}
                        <div className="pointer-events-none absolute left-2 top-2 rounded-full bg-black/50 p-1 text-white opacity-0 transition group-hover:opacity-100">
                          <GripVertical className="h-3.5 w-3.5" />
                        </div>
                        {/* Cover pin */}
                        <button
                          type="button"
                          data-testid={`main-photo-cover-${i}`}
                          onClick={() => pinMainCover(i)}
                          disabled={isCover}
                          aria-label={isCover ? "Cover photo" : "Set as cover"}
                          className={`absolute bottom-2 left-2 flex h-7 items-center gap-1 rounded-full px-2 text-[10px] font-bold uppercase tracking-widest text-white transition ${isCover ? "bg-copper" : "bg-black/60 opacity-0 group-hover:opacity-100 hover:bg-copper"}`}
                        >
                          <Star className={`h-3 w-3 ${isCover ? "fill-white" : ""}`} />
                          {isCover ? "Cover" : "Set cover"}
                        </button>
                        <button
                          type="button"
                          data-testid={`main-photo-remove-${i}`}
                          onClick={() => removePhoto(p)}
                          className="absolute right-2 top-2 rounded-full bg-white/90 p-1 text-slate-700 hover:bg-copper hover:text-white"
                          aria-label="Remove photo"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {form.photos.length > 1 && (
                <p className="text-[11px] text-slate-500">
                  Drag to reorder · the first photo is the cover guests see in search & shares. Tap the star on any photo to pin it as the cover.
                </p>
              )}
            </div>

            <div className="rounded-3xl bg-white p-6 card-shadow space-y-4">
              <h3 className="font-display text-lg font-bold">Amenities</h3>
              <p className="text-xs text-slate-500">Only tick what you actually offer — guests review honesty and mismatched amenities trigger refunds.</p>
              <div className="flex flex-wrap gap-2">
                {AMENITY_SUGGESTIONS.map((a) => (
                  <button type="button" key={a} onClick={() => toggle("amenities", a)}
                    className={`rounded-full border px-3 py-1.5 text-xs ${form.amenities.includes(a) ? "border-copper bg-copper/5 text-copper" : "border-slate-200 text-slate-600"}`}>
                    {a}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={amenityInput} onChange={(e) => setAmenityInput(e.target.value)} placeholder="Add custom amenity" className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white" />
                <button type="button" onClick={() => { if (amenityInput) { set("amenities", [...form.amenities, amenityInput]); setAmenityInput(""); } }} className="btn-outline text-xs !py-2">Add</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — Pricing & keys */}
        {step === 2 && (
          <div className="space-y-6" data-testid="wizard-panel-pricing">
            <div className="rounded-3xl bg-white p-6 card-shadow space-y-4">
              <h3 className="font-display text-lg font-bold">Pricing & duration</h3>
              <p className="text-xs text-slate-500">
                {HOUR_ONLY_TYPES.has(form.type)
                  ? "Event centres and conference rooms are priced strictly by the hour — guests book blocks of time, not overnight stays."
                  : "Tip: hourly rate is usually ~1/8 of your nightly rate. Under-price and you attract low-value walk-ins."}
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Price per hour (ZMW)</label>
                  <input data-testid="lst-hourly" type="number" value={form.price_per_hour_zmw} onChange={(e) => set("price_per_hour_zmw", parseFloat(e.target.value) || 0)} className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" />
                </div>
                {!HOUR_ONLY_TYPES.has(form.type) && (
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">Price per night (ZMW)</label>
                    <input data-testid="lst-nightly" type="number" value={form.price_per_night_zmw} onChange={(e) => set("price_per_night_zmw", parseFloat(e.target.value) || 0)} className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" />
                  </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Minimum hours (≥2)</label>
                  <input data-testid="lst-min-hours" type="number" min={2} value={form.min_hours} onChange={(e) => set("min_hours", Math.max(2, parseInt(e.target.value) || 2))} className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" />
                </div>
              </div>
            </div>

            <div className="rounded-3xl bg-white p-6 card-shadow space-y-4">
              <h3 className="font-display text-lg font-bold">Key delivery & payments</h3>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Key options *</label>
                <div className="mt-2 flex gap-2">
                  {["digital", "physical"].map((k) => (
                    <button type="button" key={k} onClick={() => toggle("key_options", k)}
                      className={`rounded-full border px-4 py-2 text-sm capitalize ${form.key_options.includes(k) ? "border-copper bg-copper/5 text-copper" : "border-slate-200 text-slate-600"}`}>
                      {k}
                    </button>
                  ))}
                </div>
              </div>
              {form.key_options.includes("physical") && (
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Physical key collection address *</label>
                  <input data-testid="lst-key-address" value={form.physical_key_address} onChange={(e) => set("physical_key_address", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white" placeholder="Reception, Riverside Lodge, Mosi-oa-Tunya Rd" />
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Accepted payments *</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { id: "mtn", label: "MTN Mobile Money" },
                    { id: "airtel", label: "Airtel Money" },
                    { id: "zamtel", label: "Zamtel Kwacha" },
                    { id: "card", label: "Card (Stripe)" },
                  ].map((m) => (
                    <button type="button" key={m.id} onClick={() => toggle("accepted_payments", m.id)}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold ${form.accepted_payments.includes(m.id) ? "border-forest bg-forest/5 text-forest" : "border-slate-200 text-slate-600"}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={step === 0 ? () => nav(-1) : back}
            data-testid="wizard-back-btn"
            className="btn-outline text-sm"
          >
            <ArrowLeft className="mr-1 inline h-3.5 w-3.5" />
            {step === 0 ? "Cancel" : "Back"}
          </button>
          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              data-testid="wizard-next-btn"
              className="btn-primary text-sm"
            >
              Next: {STEPS[step + 1].label} <ArrowRight className="ml-1 inline h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="submit"
              data-testid="lst-submit-btn"
              disabled={saving}
              className="btn-primary text-sm"
            >
              {saving ? "Publishing…" : "Publish listing"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
